// positionService.js
import { apiClient, API_ENDPOINTS } from './api';

// Кеш для должностей
let positionsCache = null;
let lastFetchTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 минут

// Флаг для предотвращения параллельных запросов
let pendingRequest = null;

export const positionService = {
  // Очистка кеша
  invalidateCache() {
    positionsCache = null;
    lastFetchTime = 0;
    pendingRequest = null;
    console.log('[positionService] Cache invalidated');
  },

  // Получить все должности с кешированием и предотвращением параллельных запросов
  async getAll(forceRefresh = false) {
    // Если есть pending запрос, возвращаем его
    if (pendingRequest) {
      console.log('[positionService] Waiting for pending request');
      return pendingRequest;
    }
    
    const now = Date.now();
    
    // Если не принудительное обновление и кеш валиден
    if (!forceRefresh && positionsCache && (now - lastFetchTime) < CACHE_TTL) {
      console.log('[positionService] Returning cached positions');
      return positionsCache;
    }
    
    try {
      console.log('[positionService] Fetching positions from API');
      
      // Создаем pending запрос
      pendingRequest = (async () => {
        const response = await apiClient.get(API_ENDPOINTS.POSITIONS);
        
        // Обработка различных форматов ответа
        let positionsArray = [];
        
        if (response && response.positions && Array.isArray(response.positions)) {
          positionsArray = response.positions;
        } else if (Array.isArray(response)) {
          positionsArray = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          positionsArray = response.data;
        } else if (response && response.data && response.data.positions && Array.isArray(response.data.positions)) {
          positionsArray = response.data.positions;
        }
        
        // Обновляем кеш
        positionsCache = positionsArray;
        lastFetchTime = Date.now();
        
        console.log(`[positionService] Loaded ${positionsArray.length} positions`);
        return positionsArray;
      })();
      
      const result = await pendingRequest;
      pendingRequest = null;
      return result;
    } catch (error) {
      pendingRequest = null;
      console.error('Ошибка загрузки должностей:', error);
      throw error;
    }
  },

  // Получить должность по ID (с использованием кеша если возможно)
  async getById(id, forceRefresh = false) {
    // Сначала пробуем получить из кеша
    if (!forceRefresh && positionsCache) {
      const cached = positionsCache.find(p => p.id === id);
      if (cached) {
        console.log(`[positionService] Returning cached position ${id}`);
        return cached;
      }
    }
    
    try {
      const response = await apiClient.get(API_ENDPOINTS.POSITION(id));
      return response.data || response;
    } catch (error) {
      console.error(`Ошибка загрузки должности ${id}:`, error);
      throw error;
    }
  },

  // Создать новую должность
  async create(positionData) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.POSITIONS, positionData);
      // Инвалидируем кеш после создания
      this.invalidateCache();
      return response.data || response;
    } catch (error) {
      console.error('Ошибка создания должности:', error);
      throw error;
    }
  },

  // Обновить должность
  async update(id, positionData) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.POSITION(id), positionData);
      // Инвалидируем кеш после обновления
      this.invalidateCache();
      return response.data || response;
    } catch (error) {
      console.error(`Ошибка обновления должности ${id}:`, error);
      throw error;
    }
  },

  // Удалить должность
  async delete(id) {
    try {
      await apiClient.delete(API_ENDPOINTS.POSITION(id));
      // Инвалидируем кеш после удаления
      this.invalidateCache();
      return true;
    } catch (error) {
      console.error(`Ошибка удаления должности ${id}:`, error);
      throw error;
    }
  },

  // Получить должности по категории
  async getByCategory(category) {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.POSITIONS}?category=${encodeURIComponent(category)}`);
      return response.data || response;
    } catch (error) {
      console.error('Ошибка загрузки должностей по категории:', error);
      throw error;
    }
  }
};